import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import '../styles/futbolistaBuscador.css';

/**
 * Componente buscador de futbolistas
 * Permite buscar y seleccionar un futbolista por nombre
 */
const FutbolistaBuscador = ({ onSelect, placeholder = "Buscar futbolista...", label = "Buscar Futbolista" }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [futbolistas, setFutbolistas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedFutbolista, setSelectedFutbolista] = useState(null);
  const searchRef = useRef(null);

  // Cerrar resultados al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar futbolistas cuando cambia el término de búsqueda
  useEffect(() => {
    const buscarFutbolistas = async () => {
      if (searchTerm.length < 2) {
        setFutbolistas([]);
        setShowResults(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`/postgres/futbolistas/search-participantes?q=${encodeURIComponent(searchTerm)}`);

        if (response.data.success) {
          setFutbolistas(response.data.data || []);
          setShowResults(true);
        }
      } catch (error) {
        console.error('Error al buscar futbolistas:', error);
        toast.error('Error al buscar futbolistas');
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(buscarFutbolistas, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleSelect = (futbolista) => {
    setSelectedFutbolista(futbolista);
    setSearchTerm(futbolista.nombre);
    setShowResults(false);
    onSelect(futbolista);
  };

  const handleClear = () => {
    setSelectedFutbolista(null);
    setSearchTerm('');
    setFutbolistas([]);
    setShowResults(false);
    onSelect(null);
  };

  return (
    <div className="futbolista-buscador" ref={searchRef}>
      {label && <label className="futbolista-buscador-label">{label}</label>}
      
      <div className="futbolista-buscador-input-container">
        <input
          type="text"
          className="futbolista-buscador-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            if (futbolistas.length > 0) {
              setShowResults(true);
            }
          }}
        />
        
        {selectedFutbolista && (
          <button
            type="button"
            className="futbolista-buscador-clear"
            onClick={handleClear}
            title="Limpiar selección"
          >
            ×
          </button>
        )}
        
        {loading && (
          <div className="futbolista-buscador-loading">
            <span className="spinner-small"></span>
          </div>
        )}
      </div>

      {showResults && futbolistas.length > 0 && (
        <div className="futbolista-buscador-results">
          {futbolistas.map((futbolista) => (
            <div
              key={futbolista.id}
              className="futbolista-buscador-result-item"
              onClick={() => handleSelect(futbolista)}
            >
              <div className="futbolista-info">
                <div className="futbolista-nombre">{futbolista.nombre}</div>
                <div className="futbolista-detalles">
                  {futbolista.fecha_nacimiento && (
                    <span>📅 {new Date(futbolista.fecha_nacimiento).toLocaleDateString('es-ES')}</span>
                  )}
                  {futbolista.genero && (
                    <span className="futbolista-genero">
                      {futbolista.genero === 'M' ? '👦' : '👧'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults && searchTerm.length >= 2 && futbolistas.length === 0 && !loading && (
        <div className="futbolista-buscador-no-results">
          No se encontraron futbolistas
        </div>
      )}
    </div>
  );
};

export default FutbolistaBuscador;

